/**
 * @file LoginPage.tsx
 * @description Email + password login. On success navigates back to the
 *   deep link the user originally requested (router state.from, set by
 *   ProtectedRoute) or to /app. Validation runs client-side via Zod and
 *   is mirrored server-side.
 * @module Pages
 * @security Client validation is UX only; the server is the trust boundary.
 *   Password field uses type="password"; never logged. `state.from` is
 *   honored only when it's an in-app absolute path ("/…", not "//…" or a
 *   full URL) — prevents open-redirect via crafted history state.
 */

import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/common/AuthLayout';
import { Button } from '../components/common/Button';
import { FormField } from '../components/common/FormField';
import { PasswordField } from '../components/common/PasswordField';
import { login } from '../lib/auth-api';
import { type FieldErrors, mapLoginError, validateCredentials } from '../lib/form-helpers';
import { useAuthStore, useSessionExpired } from '../stores/auth.store';

/** Returns the safe post-login destination: the deep link the guard
 *  captured, if it's an in-app path; otherwise the dashboard. */
function postLoginTarget(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) {
    return from;
  }
  return '/app';
}

function LoginForm(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = useSessionExpired();
  const clearSessionExpired = useAuthStore((s) => s.clearSessionExpired);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const v = validateCredentials(email, password, t);
    if (v.email !== undefined || v.password !== undefined) {
      setErrors(v);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await login({ email, password });
      clearSessionExpired();
      navigate(postLoginTarget(location.state), { replace: true });
    } catch (err) {
      setErrors({ form: mapLoginError(err, t) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4" noValidate>
      <h1 className="text-2xl font-bold text-slate-900">{t('login.title')}</h1>
      {postLoginTarget(location.state) !== '/app' ? (
        <p className="text-sm text-slate-500">{t('login.continue_hint')}</p>
      ) : null}
      {sessionExpired ? (
        <div
          role="status"
          className="text-sm border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-3"
        >
          {t('login.session_expired')}
        </div>
      ) : null}
      {errors.form !== undefined ? (
        <p role="alert" className="text-sm text-red-600">
          {errors.form}
        </p>
      ) : null}
      <FormField
        id="email"
        label={t('login.email_label')}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        required
      />
      <PasswordField
        id="password"
        label={t('login.password_label')}
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
        required
      />
      <Button type="submit" loading={loading} className="w-full">
        {t('login.submit')}
      </Button>
      <p className="text-sm text-center">
        <Link
          to="/forgot-password"
          className="text-brand-strong hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          {t('login.forgot_password_link')}
        </Link>
      </p>
    </form>
  );
}

export default function LoginPage(): JSX.Element {
  return (
    <AuthLayout>
      <LoginForm />
    </AuthLayout>
  );
}
